import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

interface TopbarContentContextValue {
  customContent: ReactNode | null;
  setCustomContent: (content: ReactNode | null) => void;
  rightContent: ReactNode | null;
  setRightContent: (content: ReactNode | null) => void;
}

const TopbarContentContext = createContext<TopbarContentContextValue | null>(
  null
);

// Global store for topbar content that works across React render boundaries
let globalTopbarContent: ReactNode | null = null;
let globalSetTopbarContent: ((content: ReactNode | null) => void) | null = null;
const topbarContentListeners = new Set<(content: ReactNode | null) => void>();

// Global store for rightContent
let globalRightContent: ReactNode | null = null;
let globalSetRightContent: ((content: ReactNode | null) => void) | null = null;
const rightContentListeners = new Set<(content: ReactNode | null) => void>();

/**
 * Global function to set topbar content from anywhere (including Backbone-rendered components)
 */
export function setTopbarContentGlobal(content: ReactNode | null): void {
  globalTopbarContent = content;
  if (globalSetTopbarContent) {
    globalSetTopbarContent(content);
  }
  // Notify all listeners
  topbarContentListeners.forEach((listener) => listener(content));
}

/**
 * Global function to set topbar rightContent from anywhere
 */
export function setTopbarRightContentGlobal(content: ReactNode | null): void {
  globalRightContent = content;
  if (globalSetRightContent) {
    globalSetRightContent(content);
  }
  // Notify all listeners
  rightContentListeners.forEach((listener) => listener(content));
}

/**
 * Subscribe to topbar content changes
 */
export function subscribeToTopbarContent(
  listener: (content: ReactNode | null) => void
): () => void {
  topbarContentListeners.add(listener);
  return () => {
    topbarContentListeners.delete(listener);
  };
}

/**
 * Subscribe to rightContent changes
 */
export function subscribeToRightContent(
  listener: (content: ReactNode | null) => void
): () => void {
  rightContentListeners.add(listener);
  // Immediately call with current value
  listener(globalRightContent);
  return () => {
    rightContentListeners.delete(listener);
  };
}

/**
 * Get current rightContent from global store (works outside React context)
 */
export function getTopbarRightContentGlobal(): ReactNode | null {
  return globalRightContent;
}

export function TopbarContentProvider({ children }: { children: ReactNode }) {
  const [customContent, setCustomContent] = useState<ReactNode | null>(
    globalTopbarContent
  );
  const [rightContent, setRightContent] = useState<ReactNode | null>(
    globalRightContent
  );

  useEffect(() => {
    globalSetTopbarContent = setCustomContent;
    // Sync with global state
    if (globalTopbarContent !== customContent) {
      setCustomContent(globalTopbarContent);
    }

    const unsubscribe = subscribeToTopbarContent((content) => {
      setCustomContent(content);
    });

    return () => {
      globalSetTopbarContent = null;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    globalSetRightContent = setRightContent;
    // Sync with global state
    if (globalRightContent !== rightContent) {
      setRightContent(globalRightContent);
    }

    const unsubscribe = subscribeToRightContent((content) => {
      setRightContent(content);
    });

    return () => {
      globalSetRightContent = null;
      unsubscribe();
    };
  }, []);

  return (
    <TopbarContentContext.Provider
      value={{ customContent, setCustomContent, rightContent, setRightContent }}
    >
      {children}
    </TopbarContentContext.Provider>
  );
}

export function useTopbarContent() {
  const context = useContext(TopbarContentContext);
  if (!context) {
    throw new Error(
      'useTopbarContent must be used within TopbarContentProvider'
    );
  }
  return context;
}
